import { selectMutableWidget, updateWidgetDataByKey, updateWidgetPropertyByPath, updateWidgetValue, useAppDispatch, useAppSelector } from '@libs/store';
import {
    ActionActionTypeEnum,
    IRootState,
    JsonPathEnum,
    StoreActionTypeEnum,
    TCalculatorData,
    TCommonInputOption,
    TextInputEnum,
    THandleChangeInputPhone,
    TInputData,
    TInputPhoneData,
    TItem,
    TUseCheckBoxHook,
    TUseInput,
    TUseInputPhone,
    TWidget,
} from '@libs/types';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useApp } from './app';
import { useBehavior } from './behavior';
import { useTriggerAction } from './hookTriggerAction';

export const isInputValid = (value: string, mandatory?: boolean, regex?: string): boolean => {
    let isValid = true;
    const trimValue = typeof value === 'string' ? value.trim() : value;
    if (mandatory && !regex) {
        isValid = !!trimValue;
    } else if (mandatory && regex) {
        try {
            const valid = new RegExp(regex);
            isValid = valid.test(trimValue);
        } catch {
            console.log('Wrong regexp expression: ', regex);
            isValid = false;
        }
    } else if (mandatory === false && regex && trimValue) {
        try {
            const valid = new RegExp(regex);
            isValid = valid.test(trimValue);
        } catch {
            console.log('Wrong regexp expression: ', regex);
            isValid = true;
        }
    }
    return isValid;
};

export const multipleValidation = (
    value: string,
    mandatory?: TInputData['mandatory'],
    regex?: TInputData['regex'],
    defaultErrorMessage?: string,
    validations?: TInputData['validations'],
): {
    isValid: boolean;
    errorMessage?: string;
} => {
    const _validations = validations ? [...validations] : [];
    if (regex || mandatory) {
        _validations.push({ regex: regex, errorMessage: defaultErrorMessage });
    }
    const validationResult = { isValid: true, errorMessage: defaultErrorMessage };

    _validations.forEach((v) => {
        if (validationResult.isValid && !isInputValid(value, mandatory, v.regex)) {
            validationResult.isValid = false;
            validationResult.errorMessage = v.errorMessage ?? defaultErrorMessage;
        }
    });

    return validationResult;
};

export const useInputPhone = (widget: TWidget<TInputPhoneData, TCommonInputOption>): TUseInputPhone => {
    const { uid } = widget;
    const data = widget.data ?? {};
    const dispatch = useAppDispatch();
    const getItemByPhoneNumber = (items: TItem[], value?: string | unknown) =>
        items.find((i) => {
            const itemValueLength = (i.value as string)?.length;
            const substring = (value as string)?.substring?.(0, itemValueLength);
            return i.id === value || i.value === substring;
        });
    const getItemByCountryIso = (items: TItem[], selectedCountryIso: string) =>
        items.find((i) => i?.countryIso?.toLowerCase?.() === selectedCountryIso.toLowerCase());

    const refDefaultRegex = useRef(data.regex);

    const countryItemForInit = useMemo(() => {
        if (Array.isArray(data.items) && data.items.length > 0) {
            const result = data.selectedCountryIso ? getItemByCountryIso(data.items, data.selectedCountryIso) : getItemByPhoneNumber(data.items, data.value);
            return result ?? getItemByPhoneNumber(data.items, data.value);
        }
    }, [data.items, data.selectedCountryIso, data.value]);

    const [iso2, setIso2] = useState<string>(countryItemForInit?.countryIso ?? '');

    useEffect(() => {
        if (countryItemForInit?.regex) {
            dispatch(
                updateWidgetDataByKey({
                    value: countryItemForInit.regex ?? refDefaultRegex.current,
                    key: 'regex',
                    uid: uid,
                }),
            );
        }
    }, [countryItemForInit, dispatch, uid]);

    const handleChangeInputPhone: THandleChangeInputPhone = (
        _value: string,
        data: Record<string, string | boolean | undefined>,
        _event?: React.SyntheticEvent<EventTarget>,
        formattedValue?: string,
    ) => {
        const clearFormattedValue = formattedValue?.replace(/\D/g, '') ?? '';
        dispatch(updateWidgetValue({ value: clearFormattedValue, uid: uid }));
        if (data.isChangeCountry) {
            setIso2(data.countryCode as string);
            dispatch(updateWidgetDataByKey({ value: data.regex ?? refDefaultRegex.current, key: 'regex', uid: uid }));
        }
    };
    return {
        iso2,
        handleChangeInputPhone,
    };
};

export const useInput = (widget: TWidget<TInputData, TCommonInputOption>): TUseInput => {
    const {
        uid,
        options,
        id,
        alternativeId,
        internalId,
        data: { mandatory, regex, validations, submitLength },
        systemMetadata,
    } = widget;
    const input = useAppSelector((state) => selectMutableWidget(state, uid));
    const { isHidden, isDisabled } = useBehavior(widget);
    const defaultErrorMessage = (input?.data as unknown as TInputData)?.errorMessage || (input?.data as unknown as TCalculatorData)?.messages?.errorMessage;
    const isFirstFocusRender = useRef(!isDisabled);
    const ref = useRef<HTMLInputElement>(null);
    const [isFocused, setIsFocused] = useState<boolean>(false);
    const [errorMessage, setErrorMessage] = useState<string | undefined>(defaultErrorMessage);
    const dispatch = useAppDispatch();
    const { triggerActionsByType } = useTriggerAction(widget);
    const { setModal } = useApp();
    const valueStr = input?.data?.value as string;
    const value = valueStr ?? '';
    const [isError, setError] = useState(input?.options?.isError);
    const isFilled = isFocused || !!value;

    useEffect(() => {
        if (options?.onFocus && ref.current) {
            ref.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
    }, [options?.onFocus]);

    useEffect(() => {
        const validationResult = multipleValidation(value, mandatory, regex, defaultErrorMessage, validations);
        if (internalId) {
            dispatch(
                updateWidgetPropertyByPath({
                    uid: uid,
                    value: validationResult.isValid,
                    path: JsonPathEnum.IS_VALID,
                }),
            );
        }
        if (systemMetadata.lastValueUpdateAction === StoreActionTypeEnum.updateWidgetValueByInternalId) {
            queueMicrotask(() => setError(!validationResult.isValid));
        }
    }, [dispatch, defaultErrorMessage, internalId, mandatory, regex, uid, validations, value, systemMetadata.lastValueUpdateAction]);

    const getClearValue = (val: string) => {
        if ((val && options?.inputType === 'number') || (val && (id === TextInputEnum.ENTERPASSCODE || alternativeId === TextInputEnum.ENTERPASSCODE))) {
            return val.replace(/\D*/g, '');
        }
        return val;
    };

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const value = getClearValue(e?.target?.value);
        setError(false);
        dispatch(updateWidgetValue({ value, uid }));
        const finalSubmitLength = submitLength ?? options?.maxLength;
        if (value?.length === finalSubmitLength) {
            triggerActionsByType(ActionActionTypeEnum.onFilled).then();
        }
    };

    const handleBlur = (e: React.FocusEvent<HTMLInputElement>) => {
        if (!isDisabled) {
            const value = getClearValue(e?.target?.value);
            const validationResult = multipleValidation(value, mandatory, regex, defaultErrorMessage, validations);
            setError(!validationResult.isValid);
            setErrorMessage(validationResult.errorMessage);
            setIsFocused(false);
        }
    };

    const handleFocus = () => {
        if (!isDisabled) {
            setIsFocused(true);
            setError(isFirstFocusRender.current && input?.options?.isError && input?.options?.onFocus);
            isFirstFocusRender.current = false;
        }
    };
    const handleClickDropdown = (item: TItem) => {
        setModal(null);
        const selectedValue = (item.value as string) ?? item.title;
        const validationResult = multipleValidation(selectedValue, mandatory, regex, defaultErrorMessage, validations);
        setError(!validationResult.isValid);
        setErrorMessage(validationResult.errorMessage);
        dispatch(updateWidgetValue({ value: selectedValue, uid }));
        triggerActionsByType(ActionActionTypeEnum.onItemSelected).then();
    };

    return {
        value,
        isFocused,
        isFilled,
        isError,
        isHidden,
        isDisabled,
        errorMessage,
        ref,
        handleChange,
        handleBlur,
        handleFocus,
        handleClickDropdown,
    };
};

export const useCheckBox = (widget: TWidget<TInputData, TCommonInputOption>): TUseCheckBoxHook => {
    const { uid } = widget;
    const dispatch = useAppDispatch();
    const checkbox = useAppSelector((state: IRootState) => selectMutableWidget(state, uid));

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const value = String(e?.target?.checked);
        dispatch(updateWidgetValue({ value, uid }));
    };
    return { handleChange, isChecked: checkbox?.data?.value === true || checkbox?.data?.value === 'true' };
};
